// src/pages/payment/PaymentSettingsPage.tsx
// Admin config, tabbed: routing rules, policies, the payment-type registry,
// and provider webhook events — rare/setup-time tasks, grouped like
// calendar's settings tabs (docs/ux/calendar-ia.md).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';
import { RoutesTab } from '@/features/payment/RoutesTab';
import { PoliciesTab } from '@/features/payment/PoliciesTab';
import { TypesTab } from '@/features/payment/TypesTab';
import { WebhookEventsTab } from '@/features/payment/WebhookEventsTab';

type Tab = 'routes' | 'policies' | 'types' | 'events';

export const PaymentSettingsPage = observer(() => {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('routes');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'routes', label: t('payment.routes.title') },
    { id: 'policies', label: t('payment.policies.title') },
    { id: 'types', label: t('payment.types.title') },
    { id: 'events', label: t('payment.events.title') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/payment/providers" className="text-muted-foreground hover:text-foreground">{t('payment.providers.title')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('payment.settings.title')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('payment.settings.title')}</h1>
      </div>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === tb.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'routes' && <RoutesTab />}
        {tab === 'policies' && <PoliciesTab />}
        {tab === 'types' && <TypesTab />}
        {tab === 'events' && <WebhookEventsTab />}
      </div>
    </div>
  );
});

export default PaymentSettingsPage;
